import React, { useCallback } from 'react';
import { Button } from '../atoms/button';
import { RegisterForm } from '../molecules/RegisterForm';

/**
 * RegisterTemplate Props
 * 
 * Template component for the registration form.
 * Pure presentation - no business logic.
 * Uses the reusable RegisterForm component from AuthDialog.
 */
export interface RegisterTemplateProps {
    /** Callback for email/password registration */
    onRegister: (name: string, email: string, password: string) => Promise<void>;

    /** Callback for Google OAuth registration */
    onGoogleAuth: () => Promise<void>;

    /** Whether the form is submitting */
    isLoading?: boolean;

    /** Whether this is the web platform (affects sizing) */
    isWeb?: boolean;

    /** Optional callback to navigate to login page */
    onNavigateToLogin?: () => void;

    /** Initial error message to display */
    initialError?: string;
}

/**
 * RegisterTemplate
 * 
 * Registration form template that reuses UI and logic from AuthDialog.
 * Now uses the extracted RegisterForm component for consistency.
 * 
 * Features:
 * - Email/password registration (via RegisterForm)
 * - Google OAuth registration
 * - Form validation (via RegisterForm)
 * - Password strength indicator (via RegisterForm)
 * - Terms & privacy policy links (via RegisterForm)
 * - Link to login page
 * 
 * Architecture:
 * - RegisterForm: Handles name/email/password form UI, validation, and password strength
 * - RegisterTemplate: Adds Google auth button and navigation links
 * 
 * Usage:
 * ```tsx
 * <RegisterTemplate
 *   onRegister={handleRegister}
 *   onGoogleAuth={handleGoogleAuth}
 *   onNavigateToLogin={() => navigate('/login')}
 *   isLoading={isLoading}
 *   isWeb={true}
 * />
 * ```
 */
export const RegisterTemplate: React.FC<RegisterTemplateProps> = ({
    onRegister,
    onGoogleAuth,
    isLoading = false,
    isWeb = true,
    onNavigateToLogin,
    initialError,
}) => {
    const handleGoogleAuth = useCallback(async () => {
        if (isLoading) return;
        await onGoogleAuth();
    }, [onGoogleAuth, isLoading]);

    return (
        <div className="w-full">
            {/* Page Title */}
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">Create Account</h1>
            <p className="text-gray-600 text-center mb-8">
                Join Ideoz and start creating amazing experiences
            </p>

            {/* Reusable Register Form with Google button */}
            <RegisterForm
                onRegister={onRegister}
                onGoogleAuth={onGoogleAuth}
                isLoading={isLoading}
                isWeb={isWeb}
                initialError={initialError}
                showPrivacyPolicy={true}
            />

            {/* Link to Login */}
            <div className="flex items-center justify-center mt-6">
                <p className="text-sm text-gray-600 font-normal">
                    Already have an account?{' '}
                    {onNavigateToLogin ? (
                        <button
                            type="button"
                            onClick={onNavigateToLogin}
                            className="text-primary-600 hover:text-primary-700 font-semibold"
                            disabled={isLoading}
                            data-testid="btn-navigate-to-login"
                        >
                            Log in
                        </button>
                    ) : (
                        <a
                            href="/login"
                            className="text-primary-600 hover:text-primary-700 font-semibold"
                            data-testid="link-to-login"
                        >
                            Log in
                        </a>
                    )}
                </p>
            </div>
        </div>
    );
};
